
import { formatDistanceToNow } from 'date-fns';
import { Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Notification } from './NotificationBell';

export interface NotificationItemProps {
  notification: Notification;
  onClick?: () => void;
  onMarkAsRead?: (id: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'order':
      return '📦';
    case 'message':
      return '💬';
    case 'review':
      return '⭐';
    case 'payment':
      return '💰';
    case 'system':
      return '🔔';
    default:
      return '🔔';
  }
};

export const NotificationItem = ({
  notification,
  onClick,
  onMarkAsRead,
  showActions = false,
  compact = false
}: NotificationItemProps) => {
  const { id, title, content, created_at, is_read, category, image_url } = notification;
  
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead(id);
    }
  };
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };
  
  const timeAgo = formatDistanceToNow(new Date(created_at), { addSuffix: true });
  const categoryIcon = getCategoryIcon(category);
  
  return (
    <div 
          className={`
            border-b last:border-b-0 cursor-pointer transition-colors border-border
            ${compact ? 'p-3' : 'p-4'}
            hover:bg-muted/50
            ${!is_read ? 'bg-primary/5' : 'bg-transparent'}
          `}
          onClick={handleClick}
        >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Avatar>
            <AvatarImage src={image_url} alt={title} />
            <AvatarFallback className="bg-secondary text-foreground">
              {title?.charAt(0) || 'N'}
            </AvatarFallback>
          </Avatar>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <h4 
              className={`font-medium text-sm text-foreground ${!is_read ? 'font-semibold' : ''}`}
            >
              {title}
            </h4>
            <span 
              className="text-xs whitespace-nowrap flex-shrink-0 text-muted-foreground"
            >
              {timeAgo}
            </span>
          </div>
          
          <p 
            className={`text-sm mt-1 text-muted-foreground ${compact ? 'line-clamp-2' : ''}`}
          >
            {content}
          </p>
          
          {showActions && !is_read && !compact && (
            <div className="mt-2 flex justify-end">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 hover:bg-muted text-foreground"
                onClick={handleMarkAsRead}
              >
                <Check className="mr-1 h-4 w-4" />
                Mark as read
              </Button>
              
              {notification.action_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2 h-8 hover:bg-muted text-foreground border-border bg-transparent"
                  onClick={handleClick}
                >
                  <ExternalLink className="mr-1 h-4 w-4" />
                  View
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
